(function () {
  'use strict';

  if (window.__covePackageDiscountKeeper) return;
  window.__covePackageDiscountKeeper = true;

  const nativeFetch = window.fetch.bind(window);
  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?$/i;
  const storageKey = 'cove-package-managed-discounts';
  let timer = null;
  let running = false;
  let queued = false;

  function normalizedPath(input) {
    try {
      const source = typeof input === 'string' ? input : input?.url;
      return new URL(source || '', window.location.origin).pathname.replace(/\/$/, '');
    } catch (error) {
      return '';
    }
  }

  function isCartMutation(input, method) {
    return String(method || 'GET').toUpperCase() !== 'GET' && cartMutationPattern.test(normalizedPath(input));
  }

  function hasInternalHeader(headers) {
    try {
      return new Headers(headers || {}).has('X-Cove-Package-Internal');
    } catch (error) {
      return false;
    }
  }

  function readStoredCodes(token) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
      return Array.isArray(stored[token]) ? stored[token] : [];
    } catch (error) {
      return [];
    }
  }

  function writeStoredCodes(token, codes) {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}');
      if (codes.length) {
        stored[token] = codes;
      } else {
        delete stored[token];
      }
      window.sessionStorage.setItem(storageKey, JSON.stringify(stored));
    } catch (error) {
      // The cart still works when sessionStorage is disabled.
    }
  }

  function uniqueCodes(codes) {
    const seen = new Set();
    return codes.reduce((result, code) => {
      const cleanCode = String(code || '').trim();
      const key = cleanCode.toLowerCase();
      if (!cleanCode || seen.has(key)) return result;
      seen.add(key);
      result.push(cleanCode);
      return result;
    }, []);
  }

  function cartRoute(endpoint) {
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root.endsWith('/') ? root : `${root}/`}cart${endpoint}`;
  }

  function packageCodes(cart) {
    return uniqueCodes((cart.items || []).map((item) => {
      if (!item.properties?._package_key) return '';
      return item.properties?._package_discount_code || '';
    }));
  }

  function appliedCodes(cart) {
    return (cart.discount_codes || []).map((entry) => ({
      code: String(entry.code || '').trim(),
      applicable: entry.applicable !== false
    })).filter((entry) => entry.code);
  }

  function packageLinesAreDiscounted(cart) {
    const packageLines = (cart.items || []).filter((item) => {
      return item.properties?._package_key && String(item.properties?._package_discount_code || '').trim();
    });

    return packageLines.length > 0 && packageLines.every((line) => {
      const originalLinePrice = Number(line.original_line_price ?? line.line_price ?? 0);
      const finalLinePrice = Number(line.final_line_price ?? line.line_price ?? 0);
      const allocationTotal = (line.line_level_discount_allocations || []).reduce((sum, allocation) => {
        return sum + Number(allocation.amount || 0);
      }, 0);

      return originalLinePrice <= 0
        || finalLinePrice < originalLinePrice
        || allocationTotal > 0
        || Number(line.line_level_total_discount || 0) > 0;
    });
  }

  function sectionIds() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return [];

    try {
      return uniqueCodes(drawer.getSectionsToRender().map((section) => section.section || section.id)).slice(0, 5);
    } catch (error) {
      return [];
    }
  }

  async function requestCart() {
    const response = await nativeFetch(cartRoute('.js'), {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Cove-Package-Internal': '1'
      }
    });
    if (!response.ok) throw new Error(`Cart request failed (${response.status}).`);
    return response.json();
  }

  async function updateDiscounts(codes) {
    const sections = sectionIds();
    const body = { discount: uniqueCodes(codes).join(',') };
    if (sections.length) {
      body.sections = sections;
      body.sections_url = window.location.pathname;
    }

    const response = await nativeFetch(cartRoute('/update.js'), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Cove-Package-Internal': '1'
      },
      body: JSON.stringify(body)
    });
    const cart = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(cart.description || cart.message || `Cart update failed (${response.status}).`);
    return cart;
  }

  // Was `cart.item_count`, the raw line count, which over-counts any cart
  // holding a package (several lines, one thing the customer added). global.js
  // owns the package-aware count and the [hidden] state.
  function updateCartCount(cart) {
    window.PovelleaCartCount?.sync(cart);
  }

  function publishCart(cart) {
    updateCartCount(cart);

    const drawer = document.querySelector('cart-drawer');
    if (cart.sections && drawer && typeof drawer.renderContents === 'function') {
      try {
        drawer.renderContents(cart);
      } catch (error) {
        console.warn('[Cove package] The cart drawer will refresh on its next normal update.', error);
      }
    }

    const detail = { cart, source: 'cove-package-discount-keeper' };
    document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
  }

  async function synchronize() {
    if (running) {
      queued = true;
      return;
    }

    running = true;
    try {
      const cart = await requestCart();
      const token = String(cart.token || 'current-cart');
      const activePackageCodes = packageCodes(cart);
      const rememberedCodes = readStoredCodes(token);
      const currentCodes = appliedCodes(cart);
      const currentCodeNames = currentCodes.map((entry) => entry.code);

      if (activePackageCodes.length) {
        writeStoredCodes(token, uniqueCodes(rememberedCodes.concat(activePackageCodes)));

        const needsUpdate = !packageLinesAreDiscounted(cart) || activePackageCodes.some((code) => {
          const match = currentCodes.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
          return !match || !match.applicable;
        });

        if (needsUpdate) {
          const updatedCart = await updateDiscounts(currentCodeNames.concat(activePackageCodes));
          publishCart(updatedCart);

          const updatedCodes = appliedCodes(updatedCart);
          const invalidCodes = activePackageCodes.filter((code) => {
            const match = updatedCodes.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
            return !match || !match.applicable;
          });
          if (invalidCodes.length || !packageLinesAreDiscounted(updatedCart)) {
            const problemCodes = invalidCodes.length ? invalidCodes : activePackageCodes;
            console.warn(`[Cove package] Shopify did not apply the package discount to every package line: ${problemCodes.join(', ')}. Check eligibility and discount combinations.`);
            document.dispatchEvent(new CustomEvent('cove:package-discount-error', {
              detail: { codes: problemCodes, cart: updatedCart }
            }));
          }
        }
      } else if (rememberedCodes.length) {
        const rememberedKeys = new Set(rememberedCodes.map((code) => code.toLowerCase()));
        const remainingCodes = currentCodeNames.filter((code) => !rememberedKeys.has(code.toLowerCase()));
        writeStoredCodes(token, []);

        if (remainingCodes.length !== currentCodeNames.length) {
          publishCart(await updateDiscounts(remainingCodes));
        }
      }
    } catch (error) {
      console.warn('[Cove package] Could not synchronize the package discount.', error);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        schedule(80);
      }
    }
  }

  function schedule(delay) {
    window.clearTimeout(timer);
    timer = window.setTimeout(synchronize, Number.isFinite(delay) ? delay : 140);
  }

  window.fetch = function (input, init) {
    const method = init?.method || (typeof input !== 'string' ? input?.method : 'GET') || 'GET';
    const internal = hasInternalHeader(init?.headers || (typeof input !== 'string' ? input?.headers : null));
    const responsePromise = nativeFetch(input, init);

    if (!internal && isCartMutation(input, method)) {
      responsePromise.then((response) => {
        if (response.ok) schedule(160);
      }).catch(() => {});
    }
    return responsePromise;
  };

  const nativeXhrOpen = XMLHttpRequest.prototype.open;
  const nativeXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__coveCartMutation = isCartMutation(url, method);
    return nativeXhrOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function () {
    if (this.__coveCartMutation) {
      this.addEventListener('loadend', () => {
        if (this.status >= 200 && this.status < 400) schedule(160);
      }, { once: true });
    }
    return nativeXhrSend.apply(this, arguments);
  };

  ['cart:updated', 'cart:change', 'product:added'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.detail?.source === 'cove-package-discount-keeper') return;
      schedule(100);
    });
  });

  window.addEventListener('pageshow', () => schedule(40));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule(80);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => schedule(20), { once: true });
  } else {
    schedule(20);
  }
})();
